import React, { useState, useEffect } from "react";
import {
  Checkbox,
  CheckboxGroup,
  Radio,
  Text,
  Paper,
  Stack,
  Divider,
  Input,
  Flex,
} from "@mantine/core";
import { SurveyQuestion } from "../../../types/profile-editing";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mantine/hooks";

type QuestionBlockProps = {
  question: SurveyQuestion;
  selectedAnswers: {
    [key: string]: { answer_ids: number[]; text_answer?: string };
  };
  onChange: (key: string, answerIds: number[], textAnswer?: string) => void;
};
export const QuestionBlock: React.FC<QuestionBlockProps> = ({
  question,
  selectedAnswers,
  onChange,
}) => {
  const questionKey = question.key;
  const { t } = useTranslation();
  const matches = useMediaQuery("(max-width: 650px)");
  const initial = selectedAnswers[questionKey];
  const initialSelected = initial?.answer_ids.map((id) => id.toString()) ?? [];
  const initialText = initial?.text_answer ?? "";

  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [textAnswer, setTextAnswer] = useState(initialText);

  const maxSelections =
    question.text.includes("Оберіть до 3") ||
    question.text.includes("Select up to 3")
      ? 3
      : undefined;

  useEffect(() => {
    const answerIds = selected.map((id) => parseInt(id));
    const otherOptionId = question.options
      .find((opt) => opt.text === t("other"))
      ?.id.toString();
    const isOtherSelected = selected.includes(otherOptionId ?? "");

    onChange(questionKey, answerIds, isOtherSelected ? textAnswer : undefined);
  }, [selected, textAnswer]);

  const hasOtherOption = question.options.some(
    (opt) => opt.text === t("other")
  );
  const isOtherSelected = (() => {
    const otherOptionId = question.options
      .find((opt) => opt.text === t("other"))
      ?.id.toString();
    return selected.includes(otherOptionId ?? "");
  })();

  const handleCheckboxChange = (newSelected: string[]) => {
    if (maxSelections && newSelected.length > maxSelections) return;
    setSelected(newSelected);
  };

  const renderOptions = () => {
    const sortedOptions = [...question.options].sort((a, b) => {
      if (a.text === t("other")) return 1;
      if (b.text === t("other")) return -1;
      return 0;
    });
    const mid = Math.ceil(sortedOptions.length / 2);
    const leftColumn = sortedOptions.slice(0, mid);
    const rightColumn = sortedOptions.slice(mid);

    const renderOption = (opt: (typeof question.options)[number]) =>
      question.selection_type === "multiple" ? (
        <Checkbox
          key={opt.id}
          value={opt.id.toString()}
          label={opt.text}
          styles={{
            label: { whiteSpace: "normal", wordBreak: "break-word" },
          }}
        />
      ) : (
        <Radio
          key={opt.id}
          value={opt.id.toString()}
          label={opt.text}
          styles={{
            label: { whiteSpace: "normal", wordBreak: "break-word" },
          }}
        />
      );

    return (
      <Flex gap="md" align="start" direction={matches ? "column" : "row"}>
        <Stack style={{ flex: 1 }} gap="xs">
          {leftColumn.map(renderOption)}
        </Stack>
        <Stack style={{ flex: 1 }} gap="xs">
          {rightColumn.map(renderOption)}
        </Stack>
      </Flex>
    );
  };

  return (
    <Paper
      shadow="sm"
      p="md"
      radius="md"
      withBorder
      id={hasOtherOption && isOtherSelected ? "" : questionKey}
    >
      <Stack gap="xs">
        <Text fw={600} fz="lg">
          {question.text}
        </Text>
        <Divider my="xs" />

        {question.selection_type === "multiple" ? (
          <CheckboxGroup value={selected} onChange={handleCheckboxChange}>
            {renderOptions()}
          </CheckboxGroup>
        ) : (
          <Radio.Group
            value={selected[0] ?? ""}
            onChange={(val) => setSelected([val])}
          >
            {renderOptions()}
          </Radio.Group>
        )}

        {hasOtherOption && isOtherSelected && (
          <Input
            id={hasOtherOption && isOtherSelected && questionKey}
            placeholder={t("please_specify")}
            value={textAnswer}
            onChange={(e) => setTextAnswer(e.currentTarget.value)}
            mt="xs"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
          />
        )}
      </Stack>
    </Paper>
  );
};
